import {
  Form,
  Input,
  DatePicker,
  AutoComplete,
  Button,
  Typography,
  Modal,
  Space,
  Popconfirm,
  Tag,
  Divider,
  Row,
  Col,
} from "antd";
import {
  Calendar,
  Trash2,
  CheckCircle2,
  XCircle,
  Pencil,
  Tag as LucideTag,
  Clock,
  Phone,
  Mail,
  Video,
  MessageSquare,
  Repeat,
  Sparkles,
  Cake,
} from "lucide-react";
import { useEffect, useState } from "react";
import { ReactComponent as WhatsAppIcon } from "../../assets/svg/sidebar/whatsapp.svg";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { searchLead } from "../../actionCreator/leadListingActionCreater";
import dayjs from "dayjs";
import { useEvent } from "./hooks/useEvent";
import { getAllEvents } from "../../actionCreator/calendarActionCreator";
import { EventStatus } from "../../config";
import { useSearchParams } from "react-router-dom";
import { getUserInfo } from "../../utils/utils";


const { Title, Text } = Typography;

export const iconMap = {
  Call: <Phone size={14} />,
  Email: <Mail size={14} />,
  Meeting: <Video size={14} />,
  Chat: <MessageSquare size={14} />,
  "Follow-up": <Repeat size={14} />,
  Followup: <Repeat size={14} />,
  Anniversary: <Sparkles size={14} />,
  Birthday: <Cake size={14} />,
  WhatsApp: <WhatsAppIcon className="tw-size-[14px]" />,
};

const AddEditEvent = ({
  isOpen,
  handleCancel,
  selectedEvent = null,
  selectedDate = null,
}) => {
  const {
    handleCreateEvent,
    handleDeleteEvent,
    handleUpdateEvent,
    handleStatusUpdate,
  } = useEvent();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const eventTypes = useSelector((state) => state.calendarReducer.eventTypes);
  const loading = useSelector((state) => state.calendarReducer.loading);
  const isDetailMode = !!selectedEvent && !isEditMode;
  const isAddMode = !selectedEvent;
  const isPast = dayjs(selectedEvent?.dueDate).isBefore(dayjs(), "day");

  const handleEditClick = () => setIsEditMode(true);

  const { id: userId } = getUserInfo();
  const refetchCallback = (callback) => {
    const allParams = {};
    for (const [key, value] of searchParams.entries()) {
      allParams[key] = value;
    }
    dispatch(
      getAllEvents({
        ...allParams,
        userId,
      }),
    );
    setIsEditMode(false); // after submit, exit edit mode
    handleCancel();
    form.resetFields();
    callback && callback();
  };

  const handleFormSubmit = (values, callback) => {
    if (isAddMode) {
      handleCreateEvent(values, () => {
        refetchCallback(callback);
      });
    }

    if (isEditMode) {
      handleUpdateEvent(
        { ...values, calendarEventId: selectedEvent?.calendarEventId },
        () => {
          refetchCallback(callback);
        },
      );
    }
  };

  const handleClose = () => {
    setIsEditMode(false);
    handleCancel();
    form.resetFields();
  };

  return (
    <Modal
      title={null}
      closable
      centered
      open={isOpen}
      onCancel={handleClose}
      footer={[]}
      className="delete-popup"
    >
      {isDetailMode ? (
        <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50">
          <div className="tw-flex tw-items-center tw-gap-3">
            <h3
              className={`tw-text-base tw-font-bold tw-text-slate-800 tw-mb-0 ${
                [EventStatus.CANCELLED].includes(selectedEvent?.status) &&
                "tw-line-through tw-text-slate-400"
              }`}
            >
              {selectedEvent.title}
            </h3>
            <Tag
              className={`!tw-border-0 !tw-text-[10px] !tw-font-bold !tw-rounded-md !tw-px-2 !tw-py-0.5 tw-m-0 tw-uppercase ${
                String(selectedEvent?.status || "").toUpperCase() ===
                "COMPLETED"
                  ? "!tw-bg-emerald-50 !tw-text-emerald-600"
                  : String(selectedEvent?.status || "").toUpperCase() ===
                      "CANCELLED"
                    ? "!tw-bg-red-50 !tw-text-red-600"
                    : "!tw-bg-indigo-50/50 !tw-text-theme"
              }`}
            >
              {selectedEvent?.status || "Pending"}
            </Tag>
          </div>
        </div>
      ) : (
        <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50">
          {isAddMode ? "Add Event" : "Edit Event"}
        </div>
      )}

      {isDetailMode ? (
        <div className="tw-p-6">
          <div className="event-detail-card">
          {/* Date & Time */}
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-6 tw-mb-5">
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-normal tw-text-slate-500">
              <Calendar size={14} className="tw-text-slate-400" />
              {selectedEvent?.dueDate && (
                <span>
                  {dayjs(selectedEvent?.dueDate).format("D MMM YYYY")}
                </span>
              )}
            </div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-normal tw-text-slate-500">
              <Clock size={14} className="tw-text-slate-400" />
              <span>{dayjs(selectedEvent?.dueDate).format("hh:mm A")}</span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-normal tw-text-slate-500">
              <LucideTag size={14} className="tw-text-slate-400" />
              <span>
                {
                  eventTypes?.find(
                    (i) =>
                      i?.calendarEventTypeId ===
                      selectedEvent?.calendarEventTypeId,
                  )?.typeName
                }
              </span>
            </div>
          </div>

          {/* Description */}
          <div className="tw-bg-slate-50/50 tw-border tw-border-solid tw-border-slate-100 tw-rounded-2xl tw-p-5 tw-flex tw-flex-col tw-gap-4.5 tw-mb-6">
            <div>
              <span className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-block tw-mb-1">
                Lead Name
              </span>
              <span className="tw-text-sm tw-font-bold tw-text-slate-700">
                {selectedEvent?.leadData?.fullname || "Not available"}
              </span>
            </div>
            <div>
              <span className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-block tw-mb-1">
                Lead Email
              </span>
              <span className="tw-text-sm tw-font-bold tw-text-slate-700">
                {selectedEvent?.leadData?.emailId || "Not available"}
              </span>
            </div>
            <div>
              <span className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-block tw-mb-1">
                Lead Company Name
              </span>
              <span className="tw-text-sm tw-font-bold tw-text-slate-700">
                {selectedEvent?.leadData?.companyName || "Not available"}
              </span>
            </div>
          </div>

          {!isPast &&
            ![EventStatus.COMPLETED].includes(selectedEvent?.status) && (
              <>
                <div className="tw-border-0 tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-slate-100 tw-pt-5">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Button
                        icon={<Pencil size={14} />}
                        onClick={handleEditClick}
                        block
                        className="secondary-btn !tw-h-[42px] !tw-rounded-xl !tw-text-xs !tw-font-bold hover:tw-scale-[1.01] tw-transition-all tw-duration-250"
                      >
                        Edit Event
                      </Button>
                    </Col>
                    {![EventStatus.COMPLETED, EventStatus.CANCELLED].includes(
                      selectedEvent?.status,
                    ) && (
                      <Col xs={24} md={12}>
                        <Popconfirm
                          title="Are you sure you want to Cancel this event?"
                          onConfirm={() => {
                            handleStatusUpdate(
                              {
                                calendarEventId: selectedEvent?.calendarEventId,
                                status: EventStatus?.CANCELLED,
                              },
                              () => {
                                refetchCallback();
                              },
                            );
                          }}
                          okText="Yes"
                          cancelText="No"
                          okButtonProps={{
                            className:
                              "theme-btn !tw-h-[32px] !tw-rounded-lg !tw-text-xs !tw-font-bold",
                          }}
                          cancelButtonProps={{
                            className:
                              "secondary-btn !tw-h-[32px] !tw-rounded-lg !tw-text-xs !tw-font-bold",
                          }}
                        >
                          <Button
                            icon={<XCircle size={14} />}
                            danger
                            block
                            className="secondary-btn !tw-h-[42px] !tw-rounded-xl !tw-text-xs !tw-font-bold hover:tw-scale-[1.01] tw-transition-all tw-duration-250"
                          >
                            Cancel Event
                          </Button>
                        </Popconfirm>
                      </Col>
                    )}
                    <Col xs={24} md={12}>
                      <Popconfirm
                        title="Are you sure you want to Delete this event?"
                        onConfirm={() =>
                          handleDeleteEvent(
                            selectedEvent?.calendarEventId,
                            () => {
                              refetchCallback();
                            },
                          )
                        }
                        okText="Yes"
                        cancelText="No"
                        okButtonProps={{
                          className:
                            "theme-btn !tw-h-[32px] !tw-rounded-lg !tw-text-xs !tw-font-bold",
                        }}
                        cancelButtonProps={{
                          className:
                            "secondary-btn !tw-h-[32px] !tw-rounded-lg !tw-text-xs !tw-font-bold",
                        }}
                        loading={loading}
                      >
                        <Button
                          icon={<Trash2 size={14} />}
                          danger
                          block
                          className="!tw-h-[42px] !tw-rounded-xl !tw-text-xs !tw-font-bold !tw-text-red-500 !tw-border-red-200 hover:!tw-bg-red-50 hover:!tw-border-red-400 hover:tw-scale-[1.01] tw-transition-all tw-duration-250"
                        >
                          Delete Event
                        </Button>
                      </Popconfirm>
                    </Col>
                    {![EventStatus.COMPLETED, EventStatus.CANCELLED].includes(
                      selectedEvent?.status,
                    ) && (
                      <Col xs={24} md={12}>
                        <Popconfirm
                          title="Are you sure you want to Mark this event as completed?"
                          onConfirm={() => {
                            handleStatusUpdate(
                              {
                                calendarEventId: selectedEvent?.calendarEventId,
                                status: EventStatus?.COMPLETED,
                              },
                              () => {
                                refetchCallback();
                              },
                            );
                          }}
                          okText="Yes"
                          cancelText="No"
                          okButtonProps={{
                            className:
                              "theme-btn !tw-h-[32px] !tw-rounded-lg !tw-text-xs !tw-font-bold",
                          }}
                          cancelButtonProps={{
                            className:
                              "secondary-btn !tw-h-[32px] !tw-rounded-lg !tw-text-xs !tw-font-bold",
                          }}
                        >
                          <Button
                            icon={<CheckCircle2 size={14} />}
                            type="primary"
                            block
                            className="theme-btn !tw-h-[42px] !tw-rounded-xl !tw-text-xs !tw-font-bold tw-shadow-[0_6px_16px_rgba(93,68,255,0.15)] hover:tw-shadow-[0_10px_24px_rgba(93,68,255,0.25)] hover:tw-scale-[1.01] tw-transition-all tw-duration-300"
                          >
                            Mark as Completed
                          </Button>
                        </Popconfirm>
                      </Col>
                    )}
                  </Row>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <EventForm
          onFinish={handleFormSubmit}
          onCancel={handleClose}
          form={form}
          initialValues={
            {
              ...selectedEvent,
              dueDate: selectedDate ? selectedDate : selectedEvent?.dueDate,
            } || {}
          }
        />
      )}
    </Modal>
  );
};

export default AddEditEvent;

const EventForm = ({ onFinish, onCancel, initialValues = {}, form }) => {
  const dispatch = useDispatch();
  const [leads, setLeads] = useState([]);
  const eventTypes = useSelector((state) => state.calendarReducer.eventTypes);
  const loading = useSelector((state) => state.calendarReducer.loading);
  const [isChanged, setIsChanged] = useState(false);
  const getSafeLeadText = (value, fallback) => {
    const normalizedValue = String(value || "").trim();

    if (
      !normalizedValue ||
      normalizedValue.toLowerCase() === "null" ||
      normalizedValue.toLowerCase() === "undefined"
    ) {
      return fallback;
    }

    return normalizedValue;
  };

  const getLeadOptionLabel = (lead) => {
    const fullName = getSafeLeadText(lead?.fullname, "");
    const firstName = getSafeLeadText(lead?.firstname, "");
    const lastName = getSafeLeadText(lead?.lastname, "");
    const combinedName = [firstName, lastName].filter(Boolean).join(" ").trim();
    const leadName = fullName || combinedName || "Unnamed Lead";
    const leadEmail = getSafeLeadText(lead?.emailId, "No email available");

    if (leadName === "Unnamed Lead" && leadEmail === "No email available") {
      return "Unnamed Lead";
    }

    if (leadName === "Unnamed Lead") {
      return leadEmail;
    }

    if (leadEmail === "No email available") {
      return leadName;
    }

    return `${leadEmail} (${leadName})`;
  };
  const onSearch = debounce(async (value) => {
    const res = await dispatch(searchLead(value));
    setLeads(res);
  }, 500);
  const selectedTypeId = Form.useWatch("calendarEventTypeId", form);

  // Populate form when initialValues change (for edit)
  useEffect(() => {
    const getData = async () => {
      const res = await dispatch(searchLead());
      setLeads(res);
    };
    getData();
    form.setFieldsValue({
      ...initialValues,
      dueDate: dayjs(initialValues?.dueDate),
    });
  }, [initialValues, form, dispatch]);

  // Watch for form changes
  const handleFormChange = () => {
    const currentValues = form.getFieldsValue(true);
    const isSame = Object.keys(initialValues).every(
      (key) => currentValues[key] === initialValues[key],
    );
    setIsChanged(!isSame);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(val) => {
        const selectedLead = leads?.find(
          (lead) => lead?.id === val?.clientLeadId,
        );

        onFinish(
          {
            ...val,
            clientLeadEmail:
              selectedLead?.emailId || initialValues?.clientLeadEmail || "",
          },
          () => form.resetFields(),
        );
      }}
      requiredMark={false}
      onValuesChange={handleFormChange}
      className="lead-form"
    >
      <div className="tw-p-6">
      <Form.Item
        label={
          <span className="tw-text-xs tw-font-bold tw-text-slate-600">
            Event Type<span className="tw-text-red-500 tw-ml-0.5">*</span>
          </span>
        }
        name="calendarEventTypeId"
        rules={[{ required: true, message: "Please select event type" }]}
      >
        <div className="tw-flex tw-flex-wrap tw-gap-2.5">
          {eventTypes.map((item) => {
            const isActive = selectedTypeId === item.calendarEventTypeId;

            return (
              <div
                key={item.calendarEventTypeId}
                className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-rounded-xl tw-text-xs tw-font-semibold tw-cursor-pointer tw-border tw-border-solid tw-transition-all tw-duration-200 ${
                  isActive
                    ? "tw-bg-theme tw-text-white tw-border-theme tw-shadow-[0_4px_12px_rgba(93,68,255,0.2)]"
                    : "tw-bg-slate-50 tw-text-slate-600 tw-border-slate-200/80 hover:tw-bg-slate-100 hover:tw-text-slate-800"
                }`}
                onClick={() => {
                  form.setFieldsValue({
                    calendarEventTypeId: item.calendarEventTypeId,
                  });
                  handleFormChange();
                }}
              >
                <div className="tw-w-3.5 tw-h-3.5 tw-flex tw-items-center">
                  {iconMap[item.typeName] || <Calendar size={14} />}
                </div>
                <span>{item.typeName}</span>
              </div>
            );
          })}
        </div>
      </Form.Item>

      <Form.Item
        name="title"
        label={
          <span className="tw-text-xs tw-font-bold tw-text-slate-600">
            Event Title<span className="tw-text-red-500 tw-ml-0.5">*</span>
          </span>
        }
        rules={[{ required: true, message: "Please add title" }]}
      >
        <Input
          placeholder="Add title"
          className="common-input !tw-h-[40px] !tw-rounded-xl !tw-border-slate-200 hover:!tw-border-slate-400 focus:!tw-border-theme"
        />
      </Form.Item>

      <Form.Item
        name="dueDate"
        label={
          <span className="tw-text-xs tw-font-bold tw-text-slate-600">
            Date and Time<span className="tw-text-red-500 tw-ml-0.5">*</span>
          </span>
        }
        rules={[{ required: true, message: "Please pick a date and time" }]}
      >
        <DatePicker
          showTime={{ format: "HH:mm" }}
          format="YYYY-MM-DD HH:mm"
          suffixIcon={<Calendar size={16} className="tw-text-slate-400" />}
          placeholder="Pick a date"
          className="tw-w-full !tw-h-[40px] !tw-rounded-xl !tw-border-slate-200 hover:!tw-border-slate-400 focus:!tw-border-theme"
        />
      </Form.Item>
      <Form.Item name="clientLeadId" hidden>
        <Input />
      </Form.Item>

      <Form.Item name="calendarEventId " hidden>
        <Input />
      </Form.Item>

      <Form.Item
        name="clientLeadEmail"
        label={
          <span className="tw-text-xs tw-font-bold tw-text-slate-600">
            Select Lead<span className="tw-text-red-500 tw-ml-0.5">*</span>
          </span>
        }
        className="tw-mb-5"
        rules={[{ required: true, message: "Please select lead" }]}
      >
        <AutoComplete
          options={leads?.map((d) => ({
            label: getLeadOptionLabel(d),
            value: getLeadOptionLabel(d),
            leadId: d?.id,
            emailId: d?.emailId || "",
          }))}
          onSearch={onSearch}
          className="tw-w-full"
          popupClassName="tw-rounded-xl"
          notFoundContent={
            <span className="tw-text-xs tw-text-slate-400 tw-p-2 tw-block">
              No leads found
            </span>
          }
          onSelect={(_, option) => {
            form.setFieldsValue({
              clientLeadEmail: option?.value,
              clientLeadId: option?.leadId,
            });
            handleFormChange();
          }}
        >
          <Input
            className="common-input !tw-h-[40px] !tw-rounded-xl !tw-border-slate-200 hover:!tw-border-slate-400 focus:!tw-border-theme"
            placeholder="Start typing a lead name"
            allowClear
          />
        </AutoComplete>
      </Form.Item>
      </div>
      <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
        <Button
          className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={!isChanged}
          className="!tw-h-[38px] !tw-rounded-lg !tw-bg-theme !tw-border-theme !tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
        >
          Save Event
        </Button>
      </div>
    </Form>
  );
};
